import axios, { AxiosRequestConfig } from "axios";
import CryptoJS from "crypto-js";
import { MainErrorInfo, hideMainErrorPopup, setMainErrorObj, showHideLoadingDialog } from "../redux/slice/commonSlice";
import { InterceptorOption } from "../types/CommonTypes";
import { FAILED_CODE_701, FAILED_CODE_702 } from "./ServiceConstants";
import { logout } from "../redux/slice/authSlice";
import { getTokenFromCookie } from "../utils/Utility";
import { CHECK_AUTHENTICATION_URL, LOGOUT_URL } from "./URL";

let dispatch: any;
let navigate: any;
let pendingRequests: number = 0;
/** While true, no request shows the global spinning loader. Used by pages that have their own progress UI. */
let skipGlobalLoader = false;

export const setSkipGlobalLoader = (skip: boolean) => {
  skipGlobalLoader = skip;
};

type HttpModule = {
  post: (url: string, data?: any, config?:AxiosRequestConfig<null>, interceptorOption?: InterceptorOption) => Promise<any>;
  get: (url: string, config?:AxiosRequestConfig<null>, interceptorOption?: InterceptorOption) => Promise<any>;
  delete: (url: string, config?:AxiosRequestConfig<null>, interceptorOption?: InterceptorOption) => Promise<any>;
  put: (url: string, data: any, config?:AxiosRequestConfig<null>, interceptorOption?: InterceptorOption) => Promise<any>;
}

export const interceptor = axios.create({
  baseURL: process.env.REACT_APP_REST_SERVICE_URL,
  headers: {
    "Content-Type": "application/json",
    "Accept": "application/json"
  } as Record<string, string>,
});

const INTERCEPTOR_OPTION_KEY = '__interceptorOption';

const withOption = (config: AxiosRequestConfig<null> | undefined, option?: InterceptorOption): AxiosRequestConfig<any> => {
  const mergedConfig: any = config || {};
  if (option) {
    mergedConfig[INTERCEPTOR_OPTION_KEY] = option;
  }
  return mergedConfig;
};

const getOption = (config: any): InterceptorOption | undefined => {
  return config?.[INTERCEPTOR_OPTION_KEY];
};

/** Axios 1.x may expose headers as AxiosHeaders; bracket access alone can miss x-skip-loader. */
const isSkipLoaderHeader = (headers: unknown): boolean => {
  if (!headers || typeof headers !== "object") {
    return false;
  }
  const h = headers as Record<string, unknown> & {
    get?: (name: string) => unknown;
  };
  const raw =
    typeof h.get === "function"
      ? h.get("x-skip-loader") ?? h.get("X-Skip-Loader")
      : h["x-skip-loader"] ?? h["X-Skip-Loader"];
  return raw === "true" || raw === true;
};

/** Sales sheet has its own progress UI, so the global blue spinner never runs on this route. */
export const isFinanceHqSalesSheetRoute = (path?: string): boolean => {
  const value = (path ?? (typeof window !== "undefined" ? window.location.pathname : ""))
    .replace(/\/+$/, "");
  return value === "/finance_hq_sales_sheet" || value.endsWith("/finance_hq_sales_sheet");
};

const shouldSkipLoader = (headers: unknown): boolean =>
  skipGlobalLoader || isFinanceHqSalesSheetRoute() || isSkipLoaderHeader(headers);

export const http: HttpModule = {
  post: (url, data, config, interceptorOption) => {
    return interceptor.post(url, data, withOption(config, interceptorOption));
  },
  get: (url, config, interceptorOption) => {
    return interceptor.get(url, withOption(config, interceptorOption));
  },
  delete: (url, config, interceptorOption) => {
    return interceptor.delete(url, withOption(config, interceptorOption));
  },
  put: (url, data, config, interceptorOption) => {
    return interceptor.put(url, data, withOption(config, interceptorOption));
  }
}

export const setDispatch = (dispatchFunction: any) => {
  dispatch = dispatchFunction;
}

export const setNavigate = (navigateFunction: any) => {
  navigate = navigateFunction;
}

interceptor.interceptors.request.use(function (config) {
    const token = getTokenFromCookie();
    if (config.url) {
      config.url = token ? appendToken(config.url, token) : config.url;
    }
     try {
      if (
        config.data &&
        config.headers["Content-Type"] === "application/json" &&
        process.env.REACT_APP_ALLOW_ENCRYPTION === "true"
      ) {
        const iv = CryptoJS.lib.WordArray.random(128 / 8).toString(
          CryptoJS.enc.Hex
        );
        const salt = CryptoJS.lib.WordArray.random(128 / 8).toString(
          CryptoJS.enc.Hex
        );
        const key = CryptoJS.PBKDF2(
          "v3biomed",
          CryptoJS.enc.Hex.parse(salt),
          {
            iterations: 1000,
            keySize: 128 / 32,
          }
        );
  
        const encrypedData = CryptoJS.AES.encrypt(
          JSON.stringify(config.data),
          key,
          {
            iv: CryptoJS.enc.Hex.parse(iv),
          }
        );
        config.data = {
          v3: salt,
          v3lite: iv,
          //@ts-ignore
          v3liteRequest: encrypedData.ciphertext.toString(CryptoJS.enc.Base64),
        };
      }
    } catch (ex) {
      console.log("ex", ex);
    }
  const option = getOption(config);
  const skipLoader = shouldSkipLoader(config.headers);
  if (!skipLoader) {
    pendingRequests++;
    if (!option?.hideLoadingPopup) {
      dispatch(showHideLoadingDialog(true));
    }
  }
    return config;
  }, function (error) {
  const skipLoader = shouldSkipLoader(error.config?.headers);
  if (!skipLoader) {
    pendingRequests--;
    // Do something with request error
    const option = getOption(error.config);
    if (!option?.hideLoadingPopup || pendingRequests === 0) {
      dispatch(showHideLoadingDialog(false));
    }
  }
    return Promise.reject(error);
  });

  interceptor.interceptors.response.use(function (response) {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    const option = getOption(response.config);
    const skipLoader = shouldSkipLoader(response.config?.headers);
    if (!skipLoader) {
      pendingRequests--;
      if (!option?.hideLoadingPopup || pendingRequests === 0) {
        dispatch(showHideLoadingDialog(false));
      }
  }
    const responseData = response.data?.responseObj || response.data;
    if (responseData?.v3) {
      try {
        const key = CryptoJS.PBKDF2(
          "v3biomed",
          CryptoJS.enc.Hex.parse(responseData.v3),
          {
            iterations: 1000,
            keySize: 128 / 32,
          }
        );
        // @ts-ignore
        const cipherParams = CryptoJS.lib.CipherParams.create({
          ciphertext: CryptoJS.enc.Base64.parse(responseData.v3liteResponse),
        });
  
        const data = CryptoJS.AES.decrypt(cipherParams, key, {
          iv: CryptoJS.enc.Hex.parse(responseData.v3lite),
        }).toString(CryptoJS.enc.Utf8);
  
        try {
          response.data = JSON.parse(data.replaceAll("â€“", "-"));
        } catch (jsonParseError) {
          console.error('JSON parse error:', jsonParseError);
          response.data = data;
        }
      } catch (decryptionError) {
        console.error('Decryption error:', decryptionError);
        // Optionally, you can handle the error or rethrow it
        throw decryptionError;
      }
    }
    if (response.data?.code !== 'V3OK'){
      setErrorToUI(response, undefined, option);
      throw(response);
    }
    return response;
  }, function (error) {
    const option = getOption(error.config);
    const skipLoader = shouldSkipLoader(error.config?.headers);
    if (!skipLoader) {
    pendingRequests--;
    if (!option?.hideLoadingPopup || pendingRequests === 0) {
      dispatch(showHideLoadingDialog(false));
    }
  }
    if (error?.response?.data?.v3) {
      const key = CryptoJS.PBKDF2(
        "v3biomed",
        CryptoJS.enc.Hex.parse(error.response.data.v3),
        {
          iterations: 1000,
          keySize: 128 / 32,
        }
      );

      //@ts-ignore
      const cipherParams = CryptoJS.lib.CipherParams.create({
        ciphertext: CryptoJS.enc.Base64.parse(
          error.response.data.v3liteResponse
        ),
      });

      const data = CryptoJS.AES.decrypt(cipherParams, key, {
        iv: CryptoJS.enc.Hex.parse(error.response.data.v3lite),
      }).toString(CryptoJS.enc.Utf8);

      try {
        error.response.data = JSON.parse(data);
      } catch {
        error.response.data = data;
      }

    }
    setErrorToUI(error?.response, error, option);
    throw error;

  });

const setErrorToUI = async (response: any, error: any, option?: InterceptorOption) => {
  if (option?.skipErrorPopup) {
    return;
  }

  if (error?.response?.status === 401 || error?.response?.status === 403 || error?.response?.status === 400
    || error?.code === "ERR_NETWORK") {
    // CS-1984: optional-chain `.url` — a network/401 error whose axios error has no `config.url`
    // previously threw TypeError here, so the user was silently NOT logged out. Guard with `?.`.
    // if (!error?.config?.url.includes(LOGOUT_URL) && !error?.config?.url.includes(CHECK_AUTHENTICATION_URL)) {
    if (!error?.config?.url?.includes(LOGOUT_URL) && !error?.config?.url?.includes(CHECK_AUTHENTICATION_URL)) {  // CS-1984
      let errorObj: MainErrorInfo = {
        title: "Oops! Something went wrong while connecting to the server.",
        errorMessageStream: "Please refresh your page using keyboard and sign back in again. \n• Use Ctrl + F5 or Ctrl + Shift + R on Windows to bypass cache.\n• Use Cmd + Shift + R on Mac",
        buttonName: "OK",
        popupType: "ServerErrorAlert",
        onCloseExt: onCloseInternalPopup,
        onOkExt: onCloseInternalPopup
      };
      dispatch(setMainErrorObj(errorObj));
      setTimeout(() => {
        dispatch(hideMainErrorPopup());
      }, 10000);
      dispatch(logout());
      navigate("/");
    }
    return;
  }

  let errorObj: MainErrorInfo = {
    title: "An error is occurred while communicating with the server."
  };
  let errorSentence = '';
  if (response?.data && typeof response.data === 'object') {
    if (response.data.code && response.data.code === FAILED_CODE_701) {
      errorObj.title = "Session is invalidated.";
      errorSentence = response.data.message && response.data.message.trim() !== "" ? response.data.message
        : "The same user has logged in from another device/browser. Only one browser session is allowed at a time."
          + "\nPlease switch to the other browser/device or log in again on this browser to continue.";
      dispatch(logout());
      navigate("/");
    } else if (response.data.code && response.data.code === FAILED_CODE_702) {
      errorObj.title = "Session is invalidated.";
      errorSentence = response.data.message && response.data.message.trim() !== "" ? response.data.message
        : "A valid user session is not found.\n You can log in again.";
      dispatch(logout());
      navigate("/");
    } else if (!response.data.errors || response.data.errors.length === 0) {
      errorSentence = response.data.message && response.data.message.trim() !== "" ? response.data.message
        : "An unknown error is occurred. Please contact a person who responsible for this.";
    } else {
      response.data.errors.forEach((error: any, index: any) => {
        if (error.field && error.errorMessage) {
          errorSentence += (!errorSentence ? `*` : `&nbsp;`) + `&nbsp;&nbsp;` + `${error.field}: ${error.errorMessage}\n`;
        } else if (error.errorMessage) {
          errorSentence += (!errorSentence ? `*` : `&nbsp;`) + `&nbsp;&nbsp;` + `${error.errorMessage}\n`;
        }
        if (error.reason) {
          errorSentence += (!errorSentence ? `*` : `&nbsp;`) + `&nbsp;&nbsp;` + `${error.reason}\n`;
        }
      });
      errorObj.title = "Please consider below issues.";
    }
  } else if (error) {
    errorSentence = `*` + error.message;
  }
  
  errorObj.errorMessageStream = errorSentence;
  dispatch(setMainErrorObj(errorObj));
  setTimeout(() => {
    dispatch(hideMainErrorPopup());
  }, 10000);
}

const appendToken = (url: string, token: string) => {
  if (url) {
    if (url.includes("?")) {
      if (url.endsWith("&")) {
        return `${url}jwtToken=Bearer ${token}`;
      } else {
        return `${url}&jwtToken=Bearer ${token}`;
      }
    } else {
      return `${url}?jwtToken=Bearer ${token}`;
    }
  }
  return url;
};

const onCloseInternalPopup = () => {
  dispatch(hideMainErrorPopup());
};
